import Vue from 'vue'
import { Component, Watch } from 'vue-property-decorator'

import { ApolloResponse } from '@/interfaces/apollo'
import { Snack, SnackType } from '@/interfaces/snack'
import { showErrors, showNotification } from '@/utils/snack'
import { randomColor } from '@/utils/color'
import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { Label } from '@/modules/Label/interfaces/label'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppSearch from '@/components/AppSearch.vue'
import AppDialog from '@/components/AppDialog.vue'
import AppTextField from '@/components/AppTextField.vue'
import AppLabelColorSelector from '@/modules/Label/components/AppLabelColorSelector.vue'
import AppTabs from '@/components/AppTabs.vue'
import AppSelector from '@/components/AppSelector.vue'
import { getLabels, newLabel, editLabel, deleteLabelById } from '@/modules/Label/gql/label-gql'
import { labelTypeKeys, labelTypeValues } from '../helpers/label'

@Component({
  components: {
    AppBreadcrumbs,
    AppSearch,
    AppDialog,
    AppTextField,
    AppLabelColorSelector,
    AppTabs,
    AppSelector
  }
})
export default class LabelList extends Vue {
  breadcrumbs: Breadcrumb[] = [
    {
      text: 'Setting'
    },
    {
      text: 'Labels'
    }
  ]
  searchVal = ''

  windowSize = {
    w: 0,
    h: 0
  }

  labels: Label[] = []

  labelInput = {
    id: 0,
    content: '',
    description: '',
    color: '',
    typeIndex: -1
  }

  renaming = false

  deleteDialog = false
  disableConfirm = false

  editDialog = false
  editLabelValid = true
  newLabelDialog = false
  newLabelValid = true

  tableLoading = false

  tableColumn = [
    { field: 'content', title: 'Label Name', slots: { default: 'name_default' }, resizable: true },
    { title: 'Description',
      field: 'description',
      sortable: false,
      resizable: true,
      slots: { default: ({ row }: any, h: any) => {
        return [
          h('span', row.description ? row.description : '-')
        ]
      }
      }
    },
    { field: 'actions', title: 'Actions', slots: { default: 'action_default' }, fixed: 'right', width: 112 }
  ]

  tabActive = 0

  tabItems = [
    {
      title: 'Ticket Label',
      type: 'TICKET'
    },
    {
      title: 'Policy Label',
      type: 'POLICY'
    },
    {
      title: 'Quote Label',
      type: 'QUOTE'
    }
  ]

  labelTypeOptions = labelTypeValues

  get filteredLabels () {
    const { searchVal, labels } = this
    if (searchVal === '') {
      return labels
    } else {
      return labels.filter((label: Label) => {
        return label.content.indexOf(searchVal) !== -1
      })
    }
  }

  get tabTitles () {
    return this.tabItems.map(item => item.title)
  }

  mounted () {
    const { type } = this.$route.params
    if (type) {
      const tabIndex = this.tabItems.findIndex((item: any) => {
        return item.type === type
      })
      this.tabActive = tabIndex
    }
    this.onResize()
    this.getLabels()
  }

  onResize () {
    this.windowSize = { w: window.innerWidth, h: window.innerHeight }
  }

  async getLabels () {
    this.tableLoading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getLabels,
        variables: {
          labelName: this.searchVal,
          type: this.tabItems[this.tabActive].type
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.tableLoading = false
      this.labels = result.data.getLabels
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  newLabel () {
    this.labelInput.color = randomColor()
    this.newLabelDialog = true
  }
  async newLabelConfirm () {
    const { labelInput } = this
    const newLabelValid = (this.$refs.newLabelForm as Vue & { validate: () => boolean }).validate()
    if (!newLabelValid) {
      return false
    }
    this.disableConfirm = true
    try {
      const result = await this.$apollo.mutate({
        mutation: newLabel,
        variables: {
          labelInput: {
            content: labelInput.content,
            color: labelInput.color,
            description: labelInput.description,
            type: labelTypeKeys[labelInput.typeIndex]
          }
        }
      })
      this.clearNewLabel()
      showNotification({
        msg: 'Label created successfully',
        type: SnackType.Success
      })
      await this.getLabels()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  renameLabel (item: any) {
    this.labelInput = this.$R.merge(this.labelInput, item)
    this.renaming = true
  }
  async renameConfirm () {
    this.renaming = false
    this.editLabelConfirm(true)
  }
  renameCancel (item: any) {
    this.renaming = false
    this.clearLabelInput()
  }
  editLabel (item: any) {
    this.labelInput = this.$R.merge(this.labelInput, item)
    this.labelInput.typeIndex = labelTypeKeys.findIndex(key => key === item.type)
    this.editDialog = true
  }

  async editLabelConfirm (notValid = false) {
    const { labelInput } = this
    if (!notValid) {
      const editValid = (this.$refs.editLabelForm as Vue & { validate: () => boolean }).validate()
      if (!editValid) {
        return
      }
    } else {
      if (labelInput.content === '') {
        return
      }
    }

    this.disableConfirm = true
    try {
      const result = await this.$apollo.mutate({
        mutation: editLabel,
        variables: {
          labelId: Number(labelInput.id),
          labelInput: {
            content: labelInput.content,
            color: labelInput.color,
            description: labelInput.description,
            type: labelTypeKeys[labelInput.typeIndex]
          }
        }
      })
      this.clearCheckedLabel()
      showNotification({
        msg: 'Label edited successfully',
        type: SnackType.Success
      })
      await this.getLabels()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  deleteLabel (item: any) {
    this.labelInput = this.$R.merge(this.labelInput, item)
    this.deleteDialog = true
  }

  async deleteLabelConfirm () {
    try {
      const result = await this.$apollo.mutate({
        mutation: deleteLabelById,
        variables: {
          labelId: Number(this.labelInput.id)
        }
      })
      this.deleteDialog = false
      this.clearLabelInput()
      const res = result.data.deleteLabelById
      if (res) {
        showNotification({
          msg: 'Label deleted successfully',
          type: SnackType.Success
        })
        await this.getLabels()
      }
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  clearLabelInput () {
    this.labelInput = {
      id: 0,
      content: '',
      description: '',
      color: '',
      typeIndex: -1
    }
  }
  clearNewLabel () {
    this.newLabelDialog = false
    this.disableConfirm = false
    this.clearLabelInput()
  }
  clearCheckedLabel () {
    this.editDialog = false
    this.disableConfirm = false
    this.clearLabelInput()
  }
  clickTabHandler (index: number) {
    this.tabActive = index
    this.getLabels()
  }
}
